import { Box, Typography } from "@mui/material";
import Home from "./Home";
import React, { useEffect, useMemo, useState } from "react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceDot, ReferenceLine,
} from "recharts";
import DeckGL from "@deck.gl/react";
import { ScatterplotLayer, BitmapLayer } from "@deck.gl/layers";
import { TileLayer } from "@deck.gl/geo-layers";
import { urlsTuilesDeckGl } from "../utilities/basemap";
import { COULEURS, RAMPE_DENSITE, enRgb } from "../theme/tribeTheme";
import { HeatmapLayer } from "@deck.gl/aggregation-layers";
import locationApi from "../utilities/locationApi";
import userApi from "../utilities/userApi";
import { useNavigate } from "react-router-dom";
import { loaderOff, loaderOn, logout, openSnackbar } from "../redux/actions";
import { useDispatch, useSelector } from "react-redux";
import store from "../redux/store";

interface Position { latitude: number; longitude: number; status?: string; createdBy?: string; locationName?: string; categoryMain?: string; locationType?: string; createdOn?: string; address?: string; delete?: string; }

/* ── Metric strip meta ── */
const METRIC_META = [
  { key: "collectee",    label: "Données collectées",  color: "#1F1E1B" },
  { key: "spotters",     label: "Spotters",             color: "#1D5FA4" },
  { key: "clients",      label: "Clients",              color: "#1D5FA4" },
  { key: "approuvees",   label: "Approuvées",           color: "#356A0F" },
  { key: "attente",      label: "En attente",           color: "#7C5600" },
  { key: "rejetees",     label: "Rejetées",             color: "#A23A0D" },
];

/* ── Custom dark tooltip ── */
function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{
      background: "var(--c-surface)",
      border: "1px solid rgba(31,30,27,0.1)",
      borderRadius: "8px",
      p: "10px 14px",
      boxShadow: "none",
    }}>
      <Typography sx={{ fontSize: "10px", color: "#57534A", mb: "4px", textTransform: "uppercase", letterSpacing: "0.08em" }}>
        {label}
      </Typography>
      <Typography sx={{ fontFamily: "var(--font-display)", fontSize: "20px", fontWeight: 700, color: "#1F1E1B", lineHeight: 1 }}>
        {(payload[0]?.value ?? 0).toLocaleString()}
      </Typography>
    </Box>
  );
}

const CATEGORY_COLORS: Record<string, string> = {
  'Commerce informel':               '#1F1E1B',
  'Restauration':                    '#60a5fa',
  'Services financiers':             '#4ade80',
  'Santé':                           '#fbbf24',
  'Éducation':                       '#f87171',
  'Services de proximité':           '#fb923c',
  'Automobile':                      '#34d399',
  'Hôtellerie & logement':           '#f472b6',
  'Culture & loisirs':               '#38bdf8',
  'Infrastructure & espace public':  '#1F1E1B',
};

const V2_CATEGORIES = [
  'Commerce informel', 'Restauration', 'Services financiers', 'Santé',
  'Éducation', 'Services de proximité', 'Automobile', 'Hôtellerie & logement',
  'Culture & loisirs', 'Infrastructure & espace public',
];

function relativeTime(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1)  return "À l'instant";
  if (mins < 60) return `Il y a ${mins} min`;
  const h = Math.floor(mins / 60);
  if (h < 24)    return `Il y a ${h} h`;
  return `Il y a ${Math.floor(h / 24)} j`;
}

/* ══════════════════════════════════════════════════════
   DASHBOARD
══════════════════════════════════════════════════════ */
export default function Dashboard() {
  const navigate   = useNavigate();
  const dispatch   = useDispatch();

  /* ── State — INCHANGÉ ── */
  /* Spotters et clients comptés par la base (GET /user/comptes), séparément
     et sans plafond : l'ancienne tuile « Comptes » comptait la liste paginée,
     limitée à 1000 lignes, clients compris. */
  const [comptes,   setComptes]   = useState<{ spotters: number; clients: number }>({ spotters: 0, clients: 0 });
  const [data,      setData]: any[] = useState([]);
  const [positions, setPositions] = useState<Position[]>([]);

  const [viewState, setViewState] = useState({
    longitude: -14.45,
    latitude: 14.33,
    zoom: 5,
    pitch: 0,
    bearing: 0,
  });

  const isLoggedIn     = useSelector((state: any) => state.auth.isLoggedIn);
  const isTokenExpired = useSelector((state: any) => state.auth.isTokenExpired);



  /* ── Statistiques calculées par le serveur (une seule définition) ──
     Remplace sept appels, dont plusieurs chargeaient toute la collection des
     POI pour compter dans le navigateur (Z1-12, Z1-13, Z1-15, Z1-17). */
  const [stats, setStats] = useState<any>(null);
  useEffect(() => {
    const init = async () => {
      dispatch(loaderOn());
      const [st, users] = await Promise.allSettled([
        locationApi.get("/location/statistiques"),
        userApi.get("/user/comptes"),
      ]);
      if (st.status === "fulfilled") {
        const s = st.value.data;
        setStats(s);
        const months = ["Jan","Fév","Mar","Avr","Mai","Jun","Jul","Aoû","Sep","Oct","Nov","Déc"];
        setData(months.map((name, i) => ({ name, valeur: Number(s?.tendanceMensuelle?.[i] ?? 0) })));
        setPoiCategories((s?.categories ?? []).slice(0, 5).map((c: any) => ({
          name: c.nom, count: Number(c.nombre) || 0, color: CATEGORY_COLORS[c.nom] || '#1F1E1B',
        })));
      } else {
        dispatch(openSnackbar("Statistiques indisponibles", "red"));
      }
      if (users.status === "fulfilled" && users.value?.data) {
        setComptes({ spotters: Number(users.value.data.spotters) || 0, clients: Number(users.value.data.clients) || 0 });
      }
      dispatch(loaderOff());
    };
    init();
  }, []);

  /* ── Positions fetch — tous les points ── */
  useEffect(() => {
    // Positions légères (sans images ni réponses), POI supprimés exclus.
    locationApi.get("/location/positions")
      .then(r => {
        const pts: Position[] = Array.isArray(r.data) ? r.data : [];
        setPositions(pts);

        // Activité récente — 3 derniers points Tribe triés par createdOn
        setRecentActivities(
          [...pts]
            .filter(p => p.createdBy !== 'osm_import' && p.createdOn)
            .sort((a, b) => new Date(b.createdOn!).getTime() - new Date(a.createdOn!).getTime())
            .slice(0, 3)
            .map(p => ({
              color: p.status === 'approved' ? '#356A0F' : p.status === 'rejected' ? '#A23A0D' : '#7C5600',
              icon:  p.status === 'approved' ? '✓' : p.status === 'rejected' ? '✕' : '◷',
              label: p.locationName || '—',
              sub:   p.address || p.categoryMain || '—',
              time:  relativeTime(p.createdOn!),
            }))
        );

      })
      .catch(() => {});
  }, []);

  /* ── Compteurs du serveur ── */
  const tribeTotal    = Number(stats?.tribe?.total ?? 0);
  const osmCount      = Number(stats?.osm ?? 0);
  const approvedCount = Number(stats?.tribe?.approuves ?? 0);
  const pendingCount  = Number(stats?.tribe?.enAttente ?? 0);
  const rejectedCount = Number(stats?.tribe?.rejetes ?? 0);
  const tribe         = { length: tribeTotal };

  /* ── Derived values — INCHANGÉ ── */
  const kpiValues: Record<string, { value: number; nav: string }> = {
    collectee:    { value: tribe.length,  nav: "/analyses/carte" },
    spotters:     { value: comptes.spotters, nav: "/reseau/spotters" },
    clients:      { value: comptes.clients,  nav: "/reglages/equipe" },
    // La page Lieux s'ouvre sur l'onglet de validation correspondant.
    approuvees:   { value: approvedCount, nav: "/lieux/fichier?onglet=approuve" },
    attente:      { value: pendingCount,  nav: "/a-traiter/lieux?onglet=attente" },
    rejetees:     { value: rejectedCount, nav: "/lieux/fichier?onglet=rejete" },
  };

  const base         = tribe.length;
  const approvalRate = base > 0 ? Math.round((approvedCount / base) * 100) : 0;
  const rejectRate   = base > 0 ? Math.round((rejectedCount / base) * 100) : 0;
  const pendingRate  = base > 0 ? Math.round((pendingCount  / base) * 100) : 0;

  /* ── Chart enrichment (UI only) ── */
  const maxMetricValue = Math.max(tribe.length, comptes.spotters, comptes.clients, approvedCount, pendingCount, rejectedCount, 1);

  const enrichedData = data.map((d: any, i: number) => ({
    ...d,
    growth:  i === 0 ? d.valeur : (d.valeur >= (data[i - 1]?.valeur ?? 0) ? d.valeur : null),
    decline: i === 0 ? null     : (d.valeur <  (data[i - 1]?.valeur ?? 0) ? d.valeur : null),
  }));

  const maxDataPoint = enrichedData.reduce(
    (mx: any, d: any) => (d.valeur > (mx?.valeur ?? 0) ? d : mx),
    null
  );

  const totalAnnuel      = data.reduce((s: number, d: any) => s + (d.valeur ?? 0), 0);
  const currentMonthIdx  = new Date().getMonth();
  const prevMonthVal     = data[currentMonthIdx - 1]?.valeur ?? 0;
  const currMonthVal     = data[currentMonthIdx]?.valeur    ?? 0;
  const monthDelta       = prevMonthVal > 0 ? Math.round(((currMonthVal - prevMonthVal) / prevMonthVal) * 100) : 0;
  const currentMonthName = data[currentMonthIdx]?.name ?? "";

  /* ── Dynamic UI data ── */
  const [typeFilter, setTypeFilter] = useState('Tous types');
  const [recentActivities, setRecentActivities] = useState<{ color: string; icon: string; label: string; sub: string; time: string }[]>([]);
  const [poiCategories, setPoiCategories]       = useState<{ name: string; count: number; color: string }[]>([]);

  const maxPoi = poiCategories.length > 0 ? poiCategories[0].count : 1;

  const filteredPositions = useMemo(() =>
    typeFilter === 'Tous types'
      ? positions
      : positions.filter(p => p.categoryMain === typeFilter),
    [positions, typeFilter]
  );

  /* ── Deck.gl layers ── */
  const layers = useMemo(() => [
    new TileLayer({
      id: "base-tiles",
      data: urlsTuilesDeckGl(),
      tileSize: 256,
      renderSubLayers: (props: any) => {
        const { west, south, east, north } = props.tile.bbox;
        return new BitmapLayer(props, {
          data: undefined,
          image: props.data,
          bounds: [west, south, east, north],
        });
      },
    }),
    new HeatmapLayer<Position>({
      id: "heatmap",
      data: filteredPositions,
      getPosition: (d) => [d.longitude, d.latitude],
      getWeight: 1,
      radiusPixels: 40,
      intensity: 1,
      threshold: 0.05,
      colorRange: RAMPE_DENSITE as any,
    }),
    new ScatterplotLayer<Position>({
      id: "scatter",
      data: filteredPositions,
      visible: viewState.zoom > 12,
      getPosition: (d) => [d.longitude, d.latitude],
      getRadius: 6,
      radiusUnits: "pixels",
      getFillColor: (d): [number, number, number] => {
        // Convention des marqueurs (charte) : référentiel bleu, approuvé vert, rejeté orange, en attente ocre.
        const st = String(d.status ?? "").toLowerCase();
        if (d.createdBy === "osm_import") return enRgb(COULEURS.info);
        if (st === "approved" || st === "validated") return enRgb(COULEURS.succes);
        if (st === "rejected")            return enRgb(COULEURS.erreur);
        return enRgb(COULEURS.attention);
      },
      pickable: true,
      opacity: 0.9,
    }),
  ], [filteredPositions, viewState.zoom]);

  /* ════════════════════════════════════
     RENDER
  ════════════════════════════════════ */
  return (
    <Box sx={{
      minHeight: "100vh",
      background: "var(--c-surface)",
      color: "#1F1E1B",
      animation: "fadeIn 0.22s ease both",
      "@keyframes fadeIn": {
        from: { opacity: 0, transform: "translateY(6px)" },
        to:   { opacity: 1, transform: "translateY(0)"   },
      },
    }}>

      {/* ─── Titre de page ─── */}
      <Box sx={{ px: "28px", pt: "22px", pb: "18px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography sx={{ fontFamily: "var(--font-display)", fontSize: "24px", fontWeight: 800, color: "#1F1E1B", letterSpacing: "-0.02em", lineHeight: 1.1 }}>
            Tableau des collectes
          </Typography>
          <Typography sx={{ fontFamily: "var(--font-body)", fontSize: "13px", color: "#57534A", mt: "4px" }}>
            Compteurs des lieux, carte de densité, évolution sur l'année et catégories.
          </Typography>
        </Box>
      </Box>

      {/* Accueil fusionné (question 15) : opérations, données, finance. */}
      <Home integre />

      {/* ═══════════════════════════════════════
          1 · METRIC STRIP — bord à bord
      ═══════════════════════════════════════ */}
      <Box sx={{
        display: "flex",
        borderTop: "1px solid rgba(31,30,27,0.05)",
        borderBottom: "1px solid rgba(31,30,27,0.05)",
        background: "var(--c-surface)",
      }}>
        {METRIC_META.map(({ key, label, color }, idx) => {
          const value    = kpiValues[key]?.value ?? 0;
          const progress = maxMetricValue > 0 ? (value / maxMetricValue) * 100 : 0;
          const isLast   = idx === METRIC_META.length - 1;

          let delta = "";
          if (key === "collectee") delta = `Tribe · importés du référentiel : ${osmCount.toLocaleString("fr-FR")}`;
          else if (key === "approuvees" && base > 0) delta = `${approvalRate}% du total Tribe`;
          else if (key === "rejetees"  && base > 0) delta = `${rejectRate}% du total Tribe`;
          else if (key === "attente"   && base > 0) delta = `${pendingRate}% en cours`;

          return (
            <Box
              key={key}
              onClick={() => navigate(kpiValues[key].nav)}
              sx={{
                flex: 1,
                px: "22px", py: "20px",
                borderRight: isLast ? "none" : "1px solid rgba(31,30,27,0.05)",
                cursor: "pointer",
                transition: "background 0.15s ease",
                "&:hover": { background: "rgba(31,30,27,0.02)" },
              }}
            >
              <Typography sx={{
                fontSize: "10px", fontWeight: 700, textTransform: "uppercase",
                letterSpacing: "0.12em", color: "#57534A", mb: "10px",
              }}>
                {label}
              </Typography>
              <Typography sx={{
                fontFamily: "var(--font-display)", fontSize: "34px", fontWeight: 800,
                color: "#1F1E1B", lineHeight: 1, letterSpacing: "-0.02em", mb: "12px",
              }}>
                {value.toLocaleString()}
              </Typography>
              {/* Progress bar 2px */}
              <Box sx={{ height: "2px", background: "rgba(31,30,27,0.06)", borderRadius: "1px", mb: "8px" }}>
                <Box sx={{
                  height: "100%", borderRadius: "1px",
                  width: `${progress}%`, background: color,
                  transition: "width 0.7s ease",
                }} />
              </Box>
              {delta && (
                <Typography sx={{ fontSize: "11px", color: "#57534A", lineHeight: 1 }}>
                  {delta}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>

      {/* ═══════════════════════════════════════
          2 · CARTE DECK.GL — plein écran 340px
      ═══════════════════════════════════════ */}
      <Box sx={{ position: "relative", height: "340px", borderBottom: "1px solid rgba(31,30,27,0.05)", background: "var(--c-surface)" }}>

        <DeckGL
          viewState={viewState}
          onViewStateChange={({ viewState: vs }: any) => setViewState(vs)}
          controller={true}
          layers={layers}
          style={{ width: "100%", height: "100%" }}
          getTooltip={({ object }: any) => object && {
            html: `<div><strong>${object.locationName ?? "—"}</strong><br/>${object.status ?? "—"}</div>`,
            style: { backgroundColor: "var(--c-surface)", color: "#1F1E1B", border: "1.5px solid #1F1E1B", padding: "8px", borderRadius: "4px", fontSize: "13px" },
          }}
        />

        {/* Filter bar flottante — centrée */}
        <Box sx={{
          position: "absolute", top: "16px", left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1000,
          display: "flex", alignItems: "center", gap: "2px",
          px: "8px", py: "5px",
          background: "var(--c-surface)",
          border: "1px solid rgba(31,30,27,0.1)",
          borderRadius: "99px",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          maxWidth: "92%",
          overflowX: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}>
          {/* Status pills */}
          {[
            // Légende de la carte (Z1-21) : couleurs de la convention des marqueurs.
            { label: "Référentiel", color: "#1D5FA4", count: osmCount      },
            { label: "Approuvé",   color: "#356A0F", count: approvedCount },
            { label: "En attente", color: "#7C5600", count: pendingCount  },
            { label: "Rejeté",     color: "#A23A0D", count: rejectedCount },
          ].map(({ label, color, count }) => (
            <Box key={label} sx={{
              display: "flex", alignItems: "center", gap: "5px",
              px: "10px", py: "5px", borderRadius: "99px",
            }}>
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: color, flexShrink: 0 }} />
              <Typography sx={{ fontSize: "11.5px", fontWeight: 600, color: "#1F1E1B", lineHeight: 1 }}>
                {label}
              </Typography>
              <Typography sx={{ fontSize: "11px", color: "#57534A", lineHeight: 1 }}>
                {count.toLocaleString()}
              </Typography>
            </Box>
          ))}

          {/* Separator */}
          <Box sx={{ width: "1px", height: "16px", background: "rgba(31,30,27,0.12)", mx: "4px", flexShrink: 0 }} />

          {/* POI type toggles — v2 taxonomy */}
          {["Tous types", ...V2_CATEGORIES].map((type) => {
            const active = typeFilter === type;
            return (
              <Box key={type} onClick={() => setTypeFilter(type)} sx={{
                px: "10px", py: "5px", borderRadius: "99px",
                cursor: "pointer",
                background: active ? "rgba(31,30,27,0.14)" : "transparent",
                border: `1px solid ${active ? "rgba(31,30,27,0.28)" : "transparent"}`,
                transition: "all 0.12s",
                "&:hover": { background: active ? "rgba(31,30,27,0.2)" : "rgba(31,30,27,0.06)" },
              }}>
                <Typography sx={{
                  fontSize: "11.5px",
                  fontWeight: active ? 600 : 400,
                  color: active ? "#1F1E1B" : "rgba(31,30,27,0.5)",
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                }}>
                  {type}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* ═══════════════════════════════════════
          3 · BOTTOM ROW — 3 colonnes
      ═══════════════════════════════════════ */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr 260px",
        gap: "1px",
        background: "rgba(31,30,27,0.04)",
      }}>

        {/* ── Col 1 · Activité récente ── */}
        <Box sx={{ background: "var(--c-surface)", p: "24px" }}>
          <Typography sx={{
            fontFamily: "var(--font-display)", fontSize: "11px", fontWeight: 700,
            color: "#57534A", textTransform: "uppercase",
            letterSpacing: "0.12em", mb: "20px",
          }}>
            Activité récente
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "18px" }}>
            {recentActivities.length === 0 && (
              <Typography sx={{ fontSize: "12px", color: "#57534A", fontStyle: "italic" }}>
                Aucune activité récente
              </Typography>
            )}
            {recentActivities.map((act, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
                  background: `${act.color}18`,
                  border: `1px solid ${act.color}28`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "13px", color: act.color, fontWeight: 700, lineHeight: 1,
                }}>
                  {act.icon}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "3px" }}>
                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#1F1E1B", lineHeight: 1 }}>
                      {act.label}
                    </Typography>
                    <Typography sx={{ fontSize: "10px", color: "#57534A", lineHeight: 1, flexShrink: 0, ml: 1 }}>
                      {act.time}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: "12px", color: "#57534A", lineHeight: 1.4 }}>
                    {act.sub}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── Col 2 · Tendance annuelle ── */}
        <Box sx={{ background: "var(--c-surface)", p: "24px" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: "18px" }}>
            <Box>
              <Typography sx={{
                fontFamily: "var(--font-display)", fontSize: "11px", fontWeight: 700,
                color: "#57534A", textTransform: "uppercase",
                letterSpacing: "0.12em", mb: "8px",
              }}>
                Collectes Tribe {stats?.annee ?? ""}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
                <Typography sx={{
                  fontFamily: "var(--font-display)", fontSize: "28px", fontWeight: 800,
                  color: "#1F1E1B", lineHeight: 1, letterSpacing: "-0.02em",
                }}>
                  {totalAnnuel.toLocaleString()}
                </Typography>
                {monthDelta !== 0 && (
                  <Typography sx={{
                    fontSize: "12px", fontWeight: 600, lineHeight: 1,
                    color: monthDelta >= 0 ? "#356A0F" : "#A23A0D",
                  }}>
                    {monthDelta >= 0 ? "+" : ""}{monthDelta}% ce mois
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          <Box sx={{ height: "160px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={enrichedData} margin={{ top: 4, right: 4, left: -32, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradMain" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="rgba(31,30,27,0.12)" stopOpacity={1} />
                    <stop offset="95%" stopColor="rgba(31,30,27,0)"    stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradGrowth" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#356A0F" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#356A0F" stopOpacity={0}   />
                  </linearGradient>
                  <linearGradient id="gradDecline" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#A23A0D" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#A23A0D" stopOpacity={0}   />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="2 4" stroke="rgba(31,30,27,0.04)" vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={{ fill: "rgba(31,30,27,0.22)", fontSize: 9, fontFamily: "var(--font-body)" }}
                  axisLine={false} tickLine={false}
                />
                <YAxis
                  tick={{ fill: "rgba(31,30,27,0.22)", fontSize: 9, fontFamily: "var(--font-body)" }}
                  axisLine={false} tickLine={false}
                />
                <Tooltip content={<ChartTooltip />} />

                {/* Ligne principale blanche */}
                <Area type="monotone" dataKey="valeur"
                  stroke="rgba(31,30,27,0.4)" strokeWidth={1.5}
                  fill="url(#gradMain)" dot={false} connectNulls />
                {/* Croissance — vert */}
                <Area type="monotone" dataKey="growth"
                  stroke="#356A0F" strokeWidth={2}
                  fill="url(#gradGrowth)" dot={false} connectNulls={false} />
                {/* Décroissance — rouge */}
                <Area type="monotone" dataKey="decline"
                  stroke="#A23A0D" strokeWidth={2}
                  fill="url(#gradDecline)" dot={false} connectNulls={false} />

                {/* Point record */}
                {maxDataPoint && (
                  <ReferenceDot
                    x={maxDataPoint.name} y={maxDataPoint.valeur}
                    r={4} fill="#1F1E1B" stroke="var(--c-surface)" strokeWidth={2}
                  />
                )}
                {/* Mois en cours */}
                {currentMonthName && (
                  <ReferenceLine
                    x={currentMonthName}
                    stroke="#1F1E1B" strokeDasharray="4 4"
                    strokeWidth={1.5} strokeOpacity={0.7}
                  />
                )}
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        </Box>

        {/* ── Col 3 · Top catégories POI ── */}
        <Box sx={{ background: "var(--c-surface)", p: "24px" }}>
          <Typography sx={{
            fontFamily: "var(--font-display)", fontSize: "11px", fontWeight: 700,
            color: "#57534A", textTransform: "uppercase",
            letterSpacing: "0.12em", mb: "20px",
          }}>
            Top catégories
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {poiCategories.map((cat) => (
              <Box key={cat.name}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "6px" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0, flex: 1 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: cat.color, flexShrink: 0 }} />
                    <Typography sx={{
                      fontSize: "12px", color: "#1F1E1B", lineHeight: 1,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>
                      {cat.name}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#1F1E1B", lineHeight: 1, flexShrink: 0, ml: "8px" }}>
                    {cat.count}
                  </Typography>
                </Box>
                <Box sx={{ height: "3px", background: "rgba(31,30,27,0.06)", borderRadius: "2px" }}>
                  <Box sx={{
                    height: "100%", borderRadius: "2px",
                    width: `${Math.round((cat.count / maxPoi) * 100)}%`,
                    background: cat.color, opacity: 0.65,
                    transition: "width 0.6s ease",
                  }} />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

      </Box>
    </Box>
  );
}
